import type { SvgIconComponent } from '@mui/icons-material';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import PriceChangeOutlinedIcon from '@mui/icons-material/PriceChangeOutlined';
//import ChildCareOutlinedIcon from '@mui/icons-material/ChildCareOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import { ROUTES } from '@/constants/routes.constants';
import { ROLES, STAFF_ROLES, type Role } from '@/constants/roles.constants';

export interface NavLeafItem {
  label: string;
  path: string;
  icon: SvgIconComponent;
  roles?: Role[];
}

export interface NavParentItem {
  label: string;
  icon: SvgIconComponent;
  children: NavLeafItem[];
  roles?: Role[];
}

export type NavItem = NavLeafItem | NavParentItem;

export function isNavParent(item: NavItem): item is NavParentItem {
  return 'children' in item;
}

// `label` holds an i18next translation key (not display text) since this config is built once
// at module scope — NavList resolves it via t() at render time so it follows the active locale.
//
// The Admin role is restricted to the Dashboard, Clients, and its own Settings (Templates, SMTP,
// Google reCAPTCHA, Child Categories — platform-wide, not per-tenant, so they're an Admin
// capability like Clients rather than an operational module), so every other item below is
// explicitly gated to STAFF_ROLES to keep it out of Admin's sidebar. Dashboard has no `roles` —
// every role, including Admin, can see it. Note there are TWO distinct "Settings" entries below:
// this Admin one (Templates, SMTP, reCAPTCHA, Child Categories) and a separate STAFF_ROLES one
// further down (Rates, Configuration) — they're mutually exclusive per-user since a role is never
// both Admin and staff, so the shared "Settings" label never actually duplicates for any one
// viewer.
export const NAV_ITEMS: NavItem[] = [
  { label: 'nav.dashboard', path: ROUTES.DASHBOARD, icon: DashboardOutlinedIcon },
  { label: 'nav.clients', path: ROUTES.CLIENTS, icon: ApartmentOutlinedIcon, roles: [ROLES.ADMIN] },
  {
    label: 'nav.settings',
    icon: SettingsOutlinedIcon,
    roles: [ROLES.ADMIN],
    children: [
      {
        label: 'nav.settingsTemplates',
        path: ROUTES.SETTINGS_TEMPLATES,
        icon: DescriptionOutlinedIcon,
      },
      {
        label: 'nav.settingsSmtp',
        path: ROUTES.SETTINGS_SMTP,
        icon: MarkEmailReadOutlinedIcon,
      },
      {
        label: 'nav.settingsRecaptcha',
        path: ROUTES.SETTINGS_RECAPTCHA,
        icon: VerifiedUserOutlinedIcon,
      },
    ],
  },
  {
    label: 'nav.reservations',
    path: ROUTES.RESERVATIONS,
    icon: EventAvailableOutlinedIcon,
    roles: STAFF_ROLES,
  },
  { label: 'nav.guests', path: ROUTES.GUESTS, icon: PeopleAltOutlinedIcon, roles: STAFF_ROLES },
  {
    label: 'nav.restaurants',
    path: ROUTES.RESTAURANTS,
    icon: StorefrontOutlinedIcon,
    roles: STAFF_ROLES,
  },
  { label: 'nav.users', path: ROUTES.USERS, icon: ManageAccountsOutlinedIcon, roles: STAFF_ROLES },
  {
    label: 'nav.logs',
    icon: ReceiptLongOutlinedIcon,
    roles: [ROLES.OWNER],
    children: [
      { label: 'nav.logsList', path: ROUTES.LOGS, icon: ReceiptLongOutlinedIcon },
      { label: 'nav.logsOccupancy', path: ROUTES.LOGS_OCCUPANCY, icon: EventAvailableOutlinedIcon },
      { label: 'nav.logsTopGuests', path: ROUTES.LOGS_TOP_GUESTS, icon: PeopleAltOutlinedIcon },
      {
        label: 'nav.logsCancellations',
        path: ROUTES.LOGS_CANCELLATIONS,
        icon: EventBusyOutlinedIcon,
      },
    ],
  },
  {
    label: 'nav.settings',
    icon: SettingsOutlinedIcon,
    roles: STAFF_ROLES,
    children: [
      { label: 'nav.settingsRates', path: ROUTES.SETTINGS_RATES, icon: PriceChangeOutlinedIcon },
     /*  {
        label: 'nav.settingsChildCategories',
        path: ROUTES.SETTINGS_CHILD_CATEGORIES,
        icon: ChildCareOutlinedIcon,
      }, */
      {
        label: 'nav.settingsConfiguration',
        path: ROUTES.SETTINGS_CONFIGURATION,
        icon: TuneOutlinedIcon,
      },
    ],
  },
];
